import React, { useEffect } from 'react';
import { classes, st } from './EmptyState.st.css';

import Heading from '../Heading';
import Text from '../Text';
import { EmptyStateProps } from './EmptyState.types';
import deprecationLog from '../utils/deprecationLog';
import { WixDesignSystemContext } from '../WixDesignSystemProvider/context';

export const defaultEmptyStateProps = {
  skin: 'section',
  image: null,
  children: null,
  align: 'center',
} as const;

/**
 * Representing a state of an empty page, section, table, etc.
 */
const EmptyState = ({
  theme,
  skin,
  image = defaultEmptyStateProps.image,
  children = defaultEmptyStateProps.children,
  align = defaultEmptyStateProps.align,
  title,
  subtitle,
  classNames: classNamesProp,
  className,
  dataHook,
}: EmptyStateProps) => {
  const mergedSkin = skin || theme || defaultEmptyStateProps.skin;
  const { mobile } = React.useContext(WixDesignSystemContext);

  useEffect(() => {
    if (theme) {
      deprecationLog(
        '<EmptyState/> - prop "theme" is deprecated and will be removed in next major release, please use "skin" property instead.',
      );
    }
  }, [theme]);

  return (
    <div
      className={st(
        classes.root,
        { skin: mergedSkin, align, mobile },
        className,
      )}
      data-hook={dataHook}
      data-skin={mergedSkin}
      data-align={align}
    >
      <div className={classes.container}>
        {image && (
          <div
            className={st(
              classes.imageContainer,
              {},
              (classNamesProp && classNamesProp.imageContainer) || '',
            )}
            data-hook="empty-state-image-container"
          >
            {typeof image === 'string' ? (
              <img
                className={classes.imageElement}
                src={image}
                data-hook="image-element"
              />
            ) : (
              React.cloneElement(image, {
                'data-hook': 'image-node',
              })
            )}
          </div>
        )}

        {title && (
          <div
            className={classes.titleContainer}
            data-hook="empty-state-title-container"
          >
            <Heading
              className={classes.title}
              as="h3"
              size={mergedSkin === 'section' ? 'medium' : 'large'}
            >
              {title}
            </Heading>
          </div>
        )}

        {subtitle && (
          <div
            className={classes.subtitleContainer}
            data-hook="empty-state-subtitle-container"
          >
            <Text className={classes.subtitle} size="small">
              {subtitle}
            </Text>
          </div>
        )}

        {children && (
          <div
            className={classes.childrenContainer}
            data-hook="empty-state-children-container"
          >
            {children}
          </div>
        )}
      </div>
    </div>
  );
};

EmptyState.displayName = 'EmptyState';
export default EmptyState;
